/**
 * @file currency.ts
 * @path packages/utils/src/formatters/currency.ts
 * @description
 */

/**
 * Formats a number into a standard currency string (e.g., "$50,000.00").
 * @example formatCurrency(50000, 'ZAR') // "R50,000.00"
 * @param amount The numeric or string amount to format.
 * @param curr The ISO 4217 currency code. Defaults to "ZAR".
 * @param locale The locale string. Defaults to "en-ZA".
 * @returns The formatted currency string, or "-" if the amount is invalid. Returns the unformatted number on Intl API error.
 */
export const formatCurrency = (
  amount: number | string | undefined,
  curr: string = "ZAR",
  locale = "en-ZA",
): string => {
  const num = Number(amount);

  if (isNaN(num) || amount === null || amount === undefined) {
    return "-";
  }

  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: curr,
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
    }).format(num);
  } catch (error) {
    return `${num}`;
  }
};

/**
 * Formats a number into a compact currency string (e.g., "$1.5M", "€12K").
 * @example formatCompactCurrency(1500000, 'ZAR') // "R1.5M"
 * @param amount The numeric or string amount to format.
 * @param curr The ISO 4217 currency code. Defaults to "ZAR".
 * @param locale The locale string. Defaults to "en-ZA".
 * @returns The formatted compact currency string, or "-" if the amount is invalid. Returns the unformatted number on Intl API error.
 */
export const formatCompactCurrency = (
  amount: number | string | undefined,
  curr: string = "ZAR",
  locale = "en-ZA",
  compact: boolean = true,
  tightSymbol: boolean = false,
): string => {
  const num = Number(amount);

  if (isNaN(num) || amount === null || amount === undefined) {
    return "-";
  }

  try {
    const formatted = new Intl.NumberFormat(locale, {
      style: "currency",
      currency: curr,
      // If compact is false, use 'standard' to show the full number
      notation: compact ? "compact" : "standard",
      compactDisplay: compact ? "short" : undefined,
      // Standard currency usually shows 2 decimals; compact usually shows 1.
      maximumFractionDigits: compact ? 1 : 2,
      minimumFractionDigits: compact ? 0 : 0,
    }).format(num);
    if (tightSymbol) {
      // 1) Strip whitespace (incl. NBSP \u00A0 and narrow NBSP \u202F) between the
      //    currency symbol and the digits  →  "R 1 240"  becomes  "R1240".
      // 2) Replace any remaining whitespace used as a thousands separator with a
      //    comma  →  "R1 240"  becomes  "R1,240".
      return formatted
        .replace(/([A-Za-z$€£¥])[\s\u00A0\u202F]+/g, "$1")
        .replace(/(\d)[\s\u00A0\u202F](\d)/g, "$1,$2");
    }
    return formatted;
  } catch (error) {
    return `${num}`;
  }
};
